/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ComboBox } from 'carbon-components-react';
import { injectIntl, defineMessages } from 'react-intl';
import { useField } from 'formik';
import { useDebounce } from 'use-debounce';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';

import { filterComboboxItems } from './comboBoxUtils';

import { TextValueFormik } from 'Common';

const propTypes = {
  /** what placeholder text should we show */
  placeholder: PropTypes.string,
  // path to field in formik context that we want to target with this input
  fieldId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/boolean-prop-naming
  disabled: PropTypes.bool,
  syncToFormikDebounceMillis: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.array,
  itemToString: PropTypes.func,
  onInputChange: PropTypes.func,

  /**
   * When true, if form input is disabled it will render as if it were
   * just a normal (potentially labelled) text value and not as a disabled input.
   * This pattern (input when enabled, plaintext when disabled) is used repeatedly
   * throughout the connect design.
   */
  renderAsTextWhenDisabled: PropTypes.bool,
};

const defaultProps = {
  disabled: false,
  placeholder: '',
  syncToFormikDebounceMillis: 500,
  items: [],
  itemToString: undefined,
  onInputChange: noop,
  renderAsTextWhenDisabled: true,
};

// limitations:
// - only supports string items

// differences from normal combobox:
//  - syncs changes to formik
//  - allows specification of items other than those in items list

export const messages = defineMessages({
  'close.menu': 'Close menu',
  'open.menu': 'Open menu',
  'clear.all': 'Clear all',
  'clear.selection': 'Clear selection',
});

const ComboBoxFormik = ({
  fieldId,
  disabled,
  placeholder,
  renderAsTextWhenDisabled,
  syncToFormikDebounceMillis,
  items,
  itemToString,
  onInputChange,
  intl: { formatMessage },
  ...others
}) => {
  const [field, meta, helpers] = useField(fieldId);

  const [value, setValue] = useState({
    // The contents of the input field
    data: field.value,

    // iff true, the value of data will be syned back to Formik state
    // once the debounce timeout expires
    syncToFormik: false,
  });

  // periodically sync changes to value back to formik state
  const [valueDebounced] = useDebounce(value, syncToFormikDebounceMillis);
  useEffect(
    () => {
      if (valueDebounced.syncToFormik) {
        helpers.setValue(valueDebounced.data);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueDebounced]
  );

  // sync changes made to formik model back to the rendered value
  // (this occurs e.g. when the form is reset, if the user selects a different event ID in the PI editor, if the user removes a row from the schema editor table, etc)
  useEffect(() => {
    // console.log('sync from formik -> rendered: ', field.value);
    setValue({
      data: field.value,
      // here we are syncing an update from formik to our locally-rendered value
      // don't propagate this change back to Formik when the debounce timeout expires
      syncToFormik: false,
    });
  }, [field.value]);

  return renderAsTextWhenDisabled && disabled ? (
    <TextValueFormik id={fieldId} hideLabel />
  ) : (
    <ComboBox
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...others}
      items={items}
      id={fieldId}
      placeholder={placeholder}
      onChange={() => true}
      translateWithId={id => formatMessage(messages[id])}
      selectedItem={isNil(value?.data) ? '' : value.data}
      itemToString={itemToString}
      shouldFilterItem={filterComboboxItems}
      onInputChange={e => {
        // and pass up the event in case the container components needs to react to this event in some way
        onInputChange(e);

        // in this case, the change was made by the user modifying the input field's contents
        // so we want to sync the change back to formik state
        setValue({
          data: e,
          syncToFormik: true,
        });
      }}
      disabled={disabled}
      invalid={!isEmpty(meta?.error)}
      invalidText={meta.error}
    />
  );
};

ComboBoxFormik.propTypes = propTypes;
ComboBoxFormik.defaultProps = defaultProps;

export default injectIntl(ComboBoxFormik);
